import React, { useState, useEffect, useContext, useRef } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Container,
  Paper,
  Avatar
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import AuthContext from '../../context/AuthContext';

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, resendVerificationEmail, loading } = useContext(AuthContext);
  
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [accountType, setAccountType] = useState('');
  const hasVerifiedRef = useRef(false); // Prevent multiple verification attempts
  
  useEffect(() => {
    // If we've already attempted verification, don't do it again
    if (hasVerifiedRef.current) {
      return;
    }
    
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');
    const accountTypeParam = searchParams.get('accountType');
    
    // Store email and accountType for potential resend
    setEmail(emailParam || '');
    setAccountType(accountTypeParam || '');
    
    if (!token || !emailParam) {
      setVerificationStatus('error');
      setMessage('Invalid verification link. Please make sure you clicked the correct link from your email.');
      return;
    }
    
    // Mark that we're attempting verification
    hasVerifiedRef.current = true;
    
    const handleVerification = async () => {
      try {
        let verifyUrl = `token=${token}&email=${emailParam}`;
        if (accountTypeParam) {
          verifyUrl += `&accountType=${accountTypeParam}`;
        }
        const result = await verifyEmail(verifyUrl);
        
        setVerificationStatus('success');
        
        // Handle different success scenarios
        if (result.alreadyVerified) {
          setMessage('Email verified successfully! You can now log in.');
        } else {
          setMessage(result.message || 'Email verified successfully!');
        }
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error) {
        setVerificationStatus('error');
        const errorData = error.response?.data;
        
        if (errorData?.expired) {
          setMessage(`${errorData.message} Click below to request a new verification email.`);
        } else {
          setMessage(errorData?.message || 'Email verification failed. Please try again.');
        }
      }
    };
    
    handleVerification();
  }, [searchParams, verifyEmail, navigate]);
  
  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail(email, accountType);
      setMessage('New verification email sent! Please check your inbox.');
    } catch (error) {
      setMessage('Failed to send verification email. Please try again.');
    }
  };
  
  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'verifying':
        return <CircularProgress size={60} />;
      case 'success':
        return <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />;
      case 'error':
        return <Error sx={{ fontSize: 60, color: 'error.main' }} />;
      default:
        return null;
    }
  };
  
  const getStatusColor = () => {
    switch (verificationStatus) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };
  
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={6} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
          <Avatar sx={{ m: 'auto', mb: 2, bgcolor: 'primary.main', width: 80, height: 80 }}>
            {getStatusIcon()}
          </Avatar>
          
          <Typography component="h1" variant="h4" gutterBottom>
            Email Verification
          </Typography>
          
          {message && (
            <Alert severity={getStatusColor()} sx={{ mb: 3 }}>
              {message}
            </Alert>
          )}
          
          {verificationStatus === 'verifying' && (
            <Typography variant="body1" color="text.secondary">
              Please wait while we verify your email address...
            </Typography>
          )}
          
          {verificationStatus === 'success' && (
            <Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Your email has been verified successfully! You can now access all features of GrooveGate.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                You will be redirected to the login page in a few seconds...
              </Typography>
              <Button
                variant="contained"
                component={RouterLink}
                to="/login"
                sx={{ mt: 2 }}
              >
                Continue to Login
              </Button>
            </Box>
          )}
          
          {verificationStatus === 'error' && (
            <Box>
              <Typography variant="body1" color="text.secondary" paragraph>
                Something went wrong with your email verification.
              </Typography>
              <Box sx={{ mt: 3 }}>
                {email && (
                  <Button
                    variant="contained"
                    onClick={handleResendVerification}
                    sx={{ mr: 2 }}
                    disabled={loading}
                  >
                    Resend Verification Email
                  </Button>
                )}
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/register"
                  sx={{ mr: 2 }}
                >
                  Try Registering Again
                </Button>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/login"
                >
                  Go to Login
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerificationPage;
